import React from 'react';
import ReactDOM from 'react-dom';

import setupUFOGlobalErrorHandler from '@atlaskit/react-ufo/global-error-handler';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { getEmptySessionData } from '@confluence/session-data/entry-points/processSessionData';
import { getSessionDataAndInitializeStatsig } from '@confluence/statsig-client/entry-points/getSessionDataAndInitializeStatsig';
import { ThemeContainer } from '@confluence/theming/entry-points/ThemeContainer';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { loadTranslation } from '@confluence/i18n/entry-points/translation';
import { BrowserNotSupportedLoader as BrowserNotSupportedPage } from '@confluence/browser-not-supported/entry-points/BrowserNotSupportedLoader';
import { initializePreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { initializeAggRegionSteeringHint } from '@confluence/query-preloader-tools/entry-points/aggRegionSteeringHint';
import { Cookies } from '@confluence/cookies';
import { initializeStorageManager } from '@confluence/storage-manager/entry-points/storageInstance';
import { applyPersistedStatsigOverrides } from '@confluence/session-data/entry-points/persistedStatsigOverrides';

import { coreBrowserNotSupported } from '@devtools/browser-check';

import { initialization } from '../../initialization';
import renderComponentIntoDOM from '../StandaloneComponentRenderer/helpers/renderComponentIntoDOM';
import { lazyLanguage } from '../../i18n/lazy-language';

import { hideMainContainer } from './fix-jank-ui';
import StandaloneConfluenceNavigationContainer from './StandaloneConfluenceNavigationContainer';

/**
 * setupUFOGlobalErrorHandler MUST be the first thing executed before any rendering operations.
 * This ensures we catch and track any errors that occur during the app's initialization,
 * including async operations and React rendering. Moving this call later would risk missing
 * early errors.
 * it provides ufo:errors:globalCount is the count of all errors which occurred on the client all up
 */
setupUFOGlobalErrorHandler();

const sessionDataPromise = (async () => {
	getApolloClient();
	try {
		const [sessionData] = await Promise.all([
			getSessionDataAndInitializeStatsig(),
			Cookies.initialize(),
		]);
		const { userId, isLicensed, monolithRegion } = sessionData;
		initializeAggRegionSteeringHint(monolithRegion);
		initializePreloaderFnContext(sessionData);
		await initializeStorageManager({ userId, isLicensed });
		applyPersistedStatsigOverrides();
		return sessionData;
	} catch {
		// For the same reasons as in the non-standalone case documented in the
		// counterpart of this file which is
		// confluence/packages/confluence-frontend-server/src/index.js at the moment
		return getEmptySessionData();
	}
})();

const translationPromise = loadTranslation(lazyLanguage);

Promise.all([sessionDataPromise, translationPromise]).then(([sessionData]) => {
	const browserNotSupportedCheck = coreBrowserNotSupported();

	// the only requirement for BrowserNotSupported to render is the translationPromise
	if (browserNotSupportedCheck) {
		// the monolith attaches this id to the body here: confluence-core/confluence-webapp/src/main/webapp/decorators/main.vmd:62
		const documentBody = document.getElementById('com-atlassian-confluence');
		// eslint-disable-next-line react/no-deprecated
		return void ReactDOM.render(<BrowserNotSupportedPage />, documentBody);
	}
	initialization(sessionData);
	hideMainContainer();
	renderComponentIntoDOM(
		<ThemeContainer>
			<RoutesContext.Consumer>
				{({ location, matchRoute }) => (
					<StandaloneConfluenceNavigationContainer
						apolloClient={getApolloClient()}
						location={location}
						matchRoute={matchRoute}
					/>
				)}
			</RoutesContext.Consumer>
		</ThemeContainer>,
		'ui-global-sidebar-section',
		true,
	);
});
